<template>
  <nav class="build-progress" aria-label="Build progress">
    <div class="build-progress-heading" aria-live="polite">
      <span>Step {{ value + 1 }} of {{ items.length }} · {{ items[value] }}</span>
      <span>{{ completedCount }} selected</span>
    </div>
    <ol ref="steps" class="build-progress-list">
      <li v-for="(item, i) in items" :key="item">
        <button
          type="button"
          class="build-step"
          :class="{ 'is-current': i === value, 'is-complete': complete(i) }"
          :aria-current="i === value ? 'step' : null"
          :aria-label="`${item}: ${i === value ? 'current step, ' : ''}${complete(i) ? 'selected' : 'pending'}`"
          :disabled="disabled[i]"
          @click="$emit('input', i)"
        >
          <span class="build-step-marker" aria-hidden="true">
            <svg v-if="icon(i)" class="build-step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path :d="icon(i)"/></svg>
            <template v-else>{{ i + 1 }}</template>
            <span v-if="complete(i)" class="build-step-check">✓</span>
          </span>
          <span>{{ item }}</span>
        </button>
      </li>
    </ol>
  </nav>
</template>

<script>
import STEP_ICONS from './stepIcons';

export default {
  props: {
    value: Number,
    items: { type: Array, default: () => [] },
    ids: { type: Array, default: () => [] },
    selected: { type: Array, default: () => [] },
    disabled: { type: Array, default: () => [] }
  },
  computed: {
    completedCount() { return this.items.filter((_, i) => this.complete(i)).length; }
  },
  watch: {
    value() { this.revealCurrent(); },
    items() { this.revealCurrent(); }
  },
  mounted() { this.revealCurrent(); },
  methods: {
    icon(i) {
      return STEP_ICONS[this.ids[i] || (i === this.items.length - 1 ? 'fit' : '')];
    },
    complete(i) {
      return this.selected.some(s => s.item && s.props.title === this.items[i]);
    },
    revealCurrent() {
      this.$nextTick(() => {
        const list = this.$refs.steps;
        const button = list && list.querySelector('[aria-current="step"]');
        if (button) {
          const left = button.offsetLeft - list.offsetLeft;
          if (left < list.scrollLeft) list.scrollLeft = left;
          else if (left + button.offsetWidth > list.scrollLeft + list.clientWidth) {
            list.scrollLeft = left + button.offsetWidth - list.clientWidth;
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.build-progress { padding: 8px 16px; background: #faf9f6; border-bottom: 1px solid #dedfd7; }
.build-progress-heading { display: flex; justify-content: flex-start; gap: 12px; font-size: 12px; color: #616759; margin-bottom: 3px; }
.build-progress-list { position: relative; display: flex; gap: 8px; overflow-x: auto; list-style: none; padding: 3px !important; margin: 0; scrollbar-width: thin; }
.build-progress-list li { flex: 0 0 auto; }
.build-step { display: flex; align-items: center; gap: 8px; min-height: 36px; padding: 4px 10px; border: 1px solid #dedfd7; border-radius: 24px; font: inherit; font-size: 13px; color: #555e4e; background: #fff; cursor: pointer; white-space: nowrap; }
.build-step-marker { position: relative; display: grid; place-items: center; width: 26px; height: 26px; border-radius: 50%; background: #eeeee7; font-size: 12px; }
.build-step-icon { width: 18px; height: 18px; }
.build-step-check { position: absolute; right: -3px; bottom: -3px; display: grid; place-items: center; width: 12px; height: 12px; border-radius: 50%; background: #344b35; color: #fff; font-size: 8px; line-height: 1; box-shadow: 0 0 0 1.5px #fff; }
.build-step.is-current .build-step-check { background: #fff; color: #344b35; box-shadow: 0 0 0 1.5px #344b35; }
.build-step.is-complete { color: #344b35; border-color: #a7b4a0; }
.build-step.is-complete .build-step-marker { background: #e4ebdf; }
.build-step.is-current { color: #fff; background: #344b35; border-color: #344b35; }
.build-step.is-current .build-step-marker { color: #fff; background: #52664b; }
.build-step:disabled { cursor: not-allowed; color: #74796d; background: #f0f0e9; }
.build-step:focus-visible { outline: 3px solid #718765; outline-offset: 2px; }
@media (max-width: 700px) {
  .build-progress { padding: 3px 9px; }
  .build-progress-heading { display: none; }
  .build-progress-list { gap: 3px; scrollbar-width: none; }
  .build-step { min-height: 38px; padding: 4px 6px; gap: 5px; font-size: 12px; }
  .build-step:not(.is-current) > span:last-child { display: none; }
  .build-step-marker { width: 30px; height: 30px; }
  .build-step-icon { width: 22px; height: 22px; }
}
</style>
